import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Clock, AlertTriangle, AlertCircle, CheckCircle, ChevronDown,
  ChevronUp, Send, Upload, Bell, ArrowRight, Scale,
  FileText, ShieldAlert, Timer, ExternalLink, RefreshCw
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const SEVERITY_STYLES = {
  critical: {
    bg: 'bg-red-50',
    border: 'border-red-300',
    text: 'text-red-700',
    badge: 'bg-red-600 text-white',
    icon: AlertTriangle,
    label: 'OVERDUE',
  },
  high: {
    bg: 'bg-amber-50',
    border: 'border-amber-300',
    text: 'text-amber-700',
    badge: 'bg-amber-500 text-white',
    icon: AlertCircle,
    label: 'URGENT',
  },
  medium: {
    bg: 'bg-yellow-50',
    border: 'border-yellow-300',
    text: 'text-yellow-700',
    badge: 'bg-yellow-500 text-white',
    icon: Clock,
    label: 'WARNING',
  },
  low: {
    bg: 'bg-green-50',
    border: 'border-green-200',
    text: 'text-green-700',
    badge: 'bg-green-600 text-white',
    icon: CheckCircle,
    label: 'ON TRACK',
  },
};

const MODULE_COLORS = {
  claim: 'bg-purple-100 text-purple-700',
  notice: 'bg-red-100 text-red-700',
  rfi: 'bg-blue-100 text-blue-700',
  si: 'bg-emerald-100 text-emerald-700',
  sd: 'bg-amber-100 text-amber-700',
};

function formatDate(dateStr) {
  if (!dateStr) return '';
  try {
    return new Date(dateStr).toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return ''; }
}

function CountdownDisplay({ countdown }) {
  const severity = SEVERITY_STYLES[countdown.severity] || SEVERITY_STYLES.low;
  const Icon = severity.icon;

  return (
    <div className={`flex items-center gap-2 px-2.5 py-1.5 rounded-sm ${severity.bg} ${severity.border} border`}>
      <Icon className={`h-4 w-4 ${severity.text} shrink-0`} />
      <div className="min-w-0">
        {countdown.is_overdue ? (
          <p className={`text-sm font-bold ${severity.text}`} data-testid="countdown-text">
            {countdown.days_overdue}d overdue
          </p>
        ) : (
          <p className={`text-sm font-bold ${severity.text}`} data-testid="countdown-text">
            {countdown.calendar_days}d left
            <span className="font-normal text-xs ml-1">({countdown.working_days}wd)</span>
          </p>
        )}
      </div>
    </div>
  );
}

function TrackerItem({ item, onSendReminder, onNavigate }) {
  const [expanded, setExpanded] = useState(false);
  const severity = SEVERITY_STYLES[item.countdown.severity] || SEVERITY_STYLES.low;

  return (
    <div
      className={`border rounded-sm transition-all ${
        item.countdown.is_overdue ? 'border-red-300 bg-red-50/30' : 'border-slate-200'
      }`}
      data-testid={`tracker-item-${item.id}`}
    >
      <div
        className="p-3 sm:p-4 flex items-center gap-3 cursor-pointer hover:bg-slate-50/50 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <CountdownDisplay countdown={item.countdown} />

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-0.5">
            <Badge className={`text-[10px] px-1.5 py-0 ${MODULE_COLORS[item.module] || 'bg-slate-100'}`}>
              {item.module_display}
            </Badge>
            <span className="text-xs font-mono font-bold text-slate-800">{item.reference}</span>
            <Badge className={`text-[10px] px-1.5 py-0 ${severity.badge}`}>{severity.label}</Badge>
          </div>
          <p className="text-sm text-slate-700 truncate">{item.title}</p>
          <p className="text-xs text-slate-400 mt-0.5">{item.awaiting_from} {item.clause ? `- ${item.clause}` : ''}</p>
        </div>

        <div className="hidden sm:flex items-center gap-2 shrink-0">
          {item.countdown.is_overdue && (
            <Button
              size="sm"
              variant="outline"
              className="border-red-300 text-red-600 hover:bg-red-50 text-xs h-7"
              onClick={(e) => { e.stopPropagation(); onSendReminder(item); }}
              data-testid={`send-reminder-${item.id}`}
            >
              <Bell className="h-3 w-3 mr-1" />
              Remind
            </Button>
          )}
          <Button
            size="sm"
            variant="ghost"
            className="text-slate-400 hover:text-slate-600 h-7 px-2"
            onClick={(e) => { e.stopPropagation(); onNavigate(item); }}
            data-testid={`navigate-${item.id}`}
          >
            <ExternalLink className="h-3.5 w-3.5" />
          </Button>
        </div>

        {expanded ? <ChevronUp className="h-4 w-4 text-slate-400 shrink-0" /> : <ChevronDown className="h-4 w-4 text-slate-400 shrink-0" />}
      </div>

      {expanded && (
        <div className="px-3 sm:px-4 pb-3 sm:pb-4 border-t border-slate-100 pt-3 space-y-3">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
            <div>
              <p className="text-slate-400 uppercase font-bold">Submitted</p>
              <p className="text-slate-700 font-medium">{formatDate(item.submitted_at)}</p>
            </div>
            <div>
              <p className="text-slate-400 uppercase font-bold">Deadline</p>
              <p className="text-slate-700 font-medium">{formatDate(item.countdown.deadline)}</p>
            </div>
            <div>
              <p className="text-slate-400 uppercase font-bold">Response Period</p>
              <p className="text-slate-700 font-medium">
                {item.response_period?.value} {item.response_period?.unit?.replace('_', ' ')}
              </p>
            </div>
            <div>
              <p className="text-slate-400 uppercase font-bold">Status</p>
              <p className="text-slate-700 font-medium">{item.status}</p>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              variant="outline"
              className="text-xs h-8"
              onClick={() => onSendReminder(item)}
              data-testid={`action-remind-${item.id}`}
            >
              <Bell className="h-3.5 w-3.5 mr-1.5" />
              Send Reminder
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="text-xs h-8"
              onClick={() => onNavigate(item)}
              data-testid={`action-view-${item.id}`}
            >
              <FileText className="h-3.5 w-3.5 mr-1.5" />
              View Details
            </Button>
            {item.countdown.is_overdue && (
              <Button
                size="sm"
                variant="outline"
                className="text-xs h-8 border-red-300 text-red-600 hover:bg-red-50"
                onClick={() => onNavigate(item)}
                data-testid={`action-escalate-${item.id}`}
              >
                <ShieldAlert className="h-3.5 w-3.5 mr-1.5" />
                Escalate
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function ResponseTrackerPanel({ projectId, moduleFilter, compact = false }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filter, setFilter] = useState(moduleFilter || '');
  const [showAll, setShowAll] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState(null);

  const fetchData = async (isManualRefresh = false) => {
    if (isManualRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (filter) params.append('module', filter);
      const res = await fetch(`${API}/api/projects/${projectId}/response-tracker?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed');
      const json = await res.json();
      setData(json);
      setLastRefreshed(new Date());
    } catch (err) {
      console.error('Response tracker error:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (!projectId) return;
    fetchData();
  }, [projectId, filter]);

  const handleSendReminder = async (item) => {
    navigate(item.url);
  };

  const handleNavigate = (item) => {
    navigate(item.url);
  };

  if (loading && !data) {
    return (
      <div className="bg-white border border-slate-200 rounded-sm p-4" data-testid="response-tracker-loading">
        <div className="flex items-center gap-2 text-slate-400">
          <Timer className="h-4 w-4 animate-pulse" />
          <span className="text-sm">Loading response tracker...</span>
        </div>
      </div>
    );
  }

  if (!data || data.total === 0) return null;

  const summary = data.summary || {};
  const items = showAll ? data.items : data.items.slice(0, compact ? 5 : 10);
  const hasMore = data.items.length > items.length;

  return (
    <div className="space-y-3" data-testid="response-tracker-panel">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Timer className="h-4 w-4 text-slate-500" />
          <h3 className="text-sm font-bold uppercase tracking-wider text-slate-600">
            Response Tracker
          </h3>
          <Badge variant="outline" className="text-xs">{data.total}</Badge>
          <span className="text-[10px] text-slate-400 hidden sm:inline">SAST</span>
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0 text-slate-400 hover:text-slate-600"
            onClick={() => fetchData(true)}
            disabled={refreshing}
            title={lastRefreshed ? `Last refreshed: ${lastRefreshed.toLocaleTimeString('en-ZA')}` : 'Refresh'}
            data-testid="refresh-tracker-btn"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`} />
          </Button>
          {!moduleFilter && (
            <div className="flex gap-1">
              {['', 'claim', 'rfi', 'si', 'notice'].map(f => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`text-[10px] font-bold uppercase px-2 py-1 rounded-sm transition-all ${
                  filter === f ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                }`}
                data-testid={`filter-${f || 'all'}`}
              >
                {f || 'All'}
              </button>
            ))}
          </div>
        )}
        </div>
      </div>

      {/* Summary chips */}
      <div className="flex gap-2 flex-wrap">
        {summary.overdue > 0 && (
          <div className="flex items-center gap-1.5 px-2.5 py-1 bg-red-50 border border-red-200 rounded-sm" data-testid="summary-overdue">
            <AlertTriangle className="h-3.5 w-3.5 text-red-500" />
            <span className="text-xs font-bold text-red-700">{summary.overdue} Overdue</span>
          </div>
        )}
        {summary.urgent > 0 && (
          <div className="flex items-center gap-1.5 px-2.5 py-1 bg-amber-50 border border-amber-200 rounded-sm" data-testid="summary-urgent">
            <AlertCircle className="h-3.5 w-3.5 text-amber-500" />
            <span className="text-xs font-bold text-amber-700">{summary.urgent} Urgent</span>
          </div>
        )}
        {summary.on_track > 0 && (
          <div className="flex items-center gap-1.5 px-2.5 py-1 bg-green-50 border border-green-200 rounded-sm" data-testid="summary-on-track">
            <CheckCircle className="h-3.5 w-3.5 text-green-500" />
            <span className="text-xs font-bold text-green-700">{summary.on_track} On Track</span>
          </div>
        )}
      </div>

      {/* Items */}
      <div className="space-y-2">
        {items.map(item => (
          <TrackerItem
            key={item.id}
            item={item}
            onSendReminder={handleSendReminder}
            onNavigate={handleNavigate}
          />
        ))}
      </div>

      {/* Show more/less */}
      {hasMore && (
        <Button
          variant="ghost"
          size="sm"
          className="w-full text-xs text-slate-500"
          onClick={() => setShowAll(!showAll)}
          data-testid="show-more-btn"
        >
          {showAll ? 'Show Less' : `Show All (${data.total})`}
        </Button>
      )}
    </div>
  );
}
